import { useEffect, useState } from "react";
import { getAllProducts } from "@/common/services/product.api";
import { IProduct } from "@/types/product.types";
import {
  DraftOrder,
  getPurchaseRequisition,
  getPurchaseRequisitionById,
} from "@/common/services/purchaseRequisition.api";
import {
  IItem,
  IPurchaseRequisition,
  PURCHASEREQUISITION_SITUATION,
  PURCHASEREQUISITION_SITUATION_LABELS,
} from "@/types/purchaseRequisition.type";
import GenericTable from "@/common/components/ui/table/GenericTable";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import { useNavigate } from "react-router-dom";

import { getCompanyId } from "@/common/services/company.api";
import ModalCreatePurchaseRequisition from "../Modal/ModalCreatePurchaseRequisition";
import { useCompanyStore } from "@/common/states/store/Company";
import Loading from "@/common/components/ui/loading/Loading";
import { getCompanySigla } from "@/common/helpers/company.helpers";
import { IVehicle } from "@/types/vehicle.type";
import TableFooter from "@/common/components/ui/table/TableFooter";
import saveAs from "file-saver";
import { PurchaseRequisitionPDF } from "../PDF/PurchaseRequisitionPDF";
import { pdf } from "@react-pdf/renderer";
import { useUser } from "@/common/services/users.api";
import ModalViewPurchaseRequisition from "../Modal/ModalViewPurchaseRequisition";
import ModalValidatePurchaseRequisition from "../Modal/ModalValidatePurchaseRequisition";
import ModalEditPurchaseRequisition from "../Modal/ModalEditPurchaseRequisition";
import { ModalGenerateOrder } from "../Modal/ModalGenerateOrder";
import ModalAuthorizedPurchaseRequisition from "../Modal/ModalAuthorizedPurchaseRequisition";

const EMPTY_ACTION = { eventName: "", data: undefined };
const headers: Record<string, string> = {
  companyId: "Cidade",
  createdAt: "Data",
  code: "Código",
  priority: "Prioridade",
  requestorName: "Solicitante",
  status: "Status",
  situation: "Situação",
  licensePlate: "Placa",
  deliveryForecast: "Previsão Entrega",
  itens: "Qtd. Itens",
};

const STATUS_LABELS = {
  PENDING: "Pendente",
  VALIDATED: "Validado",
  REJECTED: "Rejeitado",
  AUTHORIZED: "Autorizado",
  PENDING_NF: "Aguardando NF",
  COMPLETED: "Concluído",
  CANCELED: "Cancelado",
};

const statusLabels: Record<string, string> = {
  IN_PROGRESS: "Em andamento",
  PENDING: "Pendente",
  VALIDATED: "Validado",
  REJECTED: "Rejeitado",
  CONFIRMED: "Autorizado",
  PENDING_NF: "Aguardando NF",
  APPROVED: "Aprovado",
  CANCELED: "Cancelado",
};

export default function TablePurchaseRequisition() {
  const user = useUser();
  const companyId = getCompanyId();
  const company = useCompanyStore();
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const navigate = useNavigate();

  const [modal, setModal] = useState(false);
  const [products, setProducts] = useState<IProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: ["pending"],
    filterColumn: "",
    orderBy: "",
  });
  const [page, setPage] = useState<number>(1);
  const [selectedItens, setSelectedItens] = useState<IItem[]>([
    { productId: "", productName: "", quantity: "1", deliveryDate: null },
  ]);

  const [requisitions, setRequisitions] = useState<IPurchaseRequisition[]>([]);

  const handleGeneratePdf = async (requisition: IPurchaseRequisition) => {
    try {
      const response = await getPurchaseRequisitionById(requisition._id);
      const fullData = response.data;

      const blob = await pdf(
        <PurchaseRequisitionPDF data={fullData} />
      ).toBlob();

      saveAs(blob, `requisicao-${fullData._id}.pdf`);
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
    }
  };
  const cleanActionData = () => {
    handleSearch(filters);
    setAction(EMPTY_ACTION);
  };

  const handleSearch = (newFilters: Partial<typeof filters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  };

  const loadPurchaseRequisitions = async () => {
    const result = await getPurchaseRequisition({
      page,
      orderBy: "-createdAt",
      status: filters.status,
      search: filters.search,
    });
    setRequisitions(result.data);
    setData({ itemsPerPage: 10, totalItems: result.totalItems });
  };

  const openModal = () => setModal(true);
  const closeModal = () => {
    setModal(false);
    loadPurchaseRequisitions();
  };

  const handleAddItemLine = () => {
    setSelectedItens((prev) => [
      ...prev,
      { productId: "", productName: "", quantity: "1" },
    ]);
  };
  const getLatestDeliveryDate = (itens: IItem[]): string => {
    if (!itens || itens.length === 0) return "N/A";

    const dates = itens
      .map((item) => (item.deliveryDate ? new Date(item.deliveryDate) : null))
      .filter((date): date is Date => date !== null);

    if (dates.length === 0) return "N/A";

    const latestDate = new Date(
      Math.max.apply(
        null,
        dates.map((d) => d.getTime())
      )
    );

    return latestDate.toLocaleDateString();
  };
  const removeItem = (index: number) => {
    setSelectedItens((prev) => prev.filter((_, i) => i !== index));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateItem = (index: number, field: keyof IItem, value: any) => {
    const updated = [...selectedItens];
    updated[index][field] = value;
    setSelectedItens(updated);
  };

  useEffect(() => {
    async function loadProducts() {
      setIsLoading(true);
      const result = await getAllProducts();
      if (result.data) {
        setProducts(result.data);
      }
      setIsLoading(false);
    }
    loadProducts();
  }, []);

  useEffect(() => {
    loadPurchaseRequisitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, company, filters, page]);

  const renderItens = (data: unknown): JSX.Element => {
    const itens = data as IItem[];
    return <span>{itens.length}</span>;
  };

  const actions = [
    {
      label: "Visualizar",
      action: (data) => setAction({ eventName: "view", data }),
    },
    {
      label: "Gerar PDF",
      action: (data) => handleGeneratePdf(data as IPurchaseRequisition),
    },
    {
      label: "Editar",
      visible: (data) => {
        const status = (data as IPurchaseRequisition).status;
        const requestorName = (data as IPurchaseRequisition).requestorName;
        return (
          (status === "PENDING" || status === "REJECTED") &&
          requestorName === user?.name
        );
      },
      action: (data) => setAction({ eventName: "edit", data }),
    },
    {
      label: "Validar",
      action: (data) => setAction({ eventName: "validated", data }),
      permission: "status:vehicles",
      visible: (data) => data.situation !== "APPROVED",
    },
    {
      label: "Autorizar",
      action: (data) => setAction({ eventName: "authorized", data }),
      visible: (data) =>
        data.situation === "PARTIAL_QUOTED" ||
        data.situation === "FULLY_QUOTED" ||
        data.situation === "PARTIAL_APPROVED",
    },
    {
      label: "Gerar ordem de compra",
      action: (data) => setAction({ eventName: "order", data }),
      visible: (data) =>
        data.situation === "PARTIAL_APPROVED" ||
        data.situation === "APPROVED" ||
        data.status === "PARTIAL_APPROVED" ||
        data.status === "APPROVED",
    },
  ];

  const isAction = (eventName: string) => action.eventName === eventName;

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  return (
    <div className="table-section">
      <TableFilters
        dashboardHeader="Requisições de Compras"
        placeholder="Buscar pelo solicitante..."
        newItemLabel="Nova Requisição"
        statusOptions={STATUS_LABELS}
        onNewItemClick={openModal}
        onSearch={handleFilterChange}
        entity="purchase-requisition"
        tableData={requisitions}
      />
      <GenericTable
        headers={headers}
        data={requisitions}
        setFilters={() => {}}
        actions={actions}
        renders={{
          companyId: (companyId) => {
            return getCompanySigla(companyId as string) || "";
          },
          createdAt: (createdAt) => {
            return new Date(createdAt as string).toLocaleDateString();
          },
          itens: renderItens,
          licensePlate: (licensePlate, vehicle) => {
            if (licensePlate) {
              return (
                <a
                  className="text-blue-600 hover:text-bluePrimary cursor-pointer"
                  onClick={() =>
                    navigate(`/veiculos/${(vehicle as IVehicle).licensePlate}`)
                  }
                  target="_blank"
                >
                  {String(licensePlate)}
                </a>
              );
            } else {
              return <span>-</span>;
            }
          },
          priority: (priority) =>
            priority === "HIGH" ? (
              <span className="text-red-500 font-bold">Alta</span>
            ) : priority === "MEDIUM" ? (
              <span className="text-yellow-500 font-bold">Média</span>
            ) : (
              <span className="text-green-500 font-bold">Baixa</span>
            ),
          deliveryForecast: (_, row) =>
            getLatestDeliveryDate((row as IPurchaseRequisition).itens),
          status: (status) =>
            typeof status === "string" ? (
              <span className="flex items-center gap-2">
                <span
                  className={`w-2 h-2 rounded-full ${
                    status === "PENDING"
                      ? "bg-yellow-400"
                      : status === "APPROVED"
                      ? "bg-green-500"
                      : "bg-gray-400"
                  }`}
                />
                {statusLabels[status] || status}
              </span>
            ) : (
              String(status)
            ),
          situation: (situation) => (
            <span>
              {
                PURCHASEREQUISITION_SITUATION_LABELS[
                  situation as PURCHASEREQUISITION_SITUATION
                ]
              }
            </span>
          ),
        }}
        entity="vehicles"
      />

      <TableFooter
        page={page || 1}
        totalItems={data.totalItems || 0}
        itemsPerPage={data.itemsPerPage || 10}
        goToPage={setPage}
      />

      {isLoading && <Loading />}

      {isAction("validated") && (
        <ModalValidatePurchaseRequisition
          action={action.eventName}
          data={action.data as IPurchaseRequisition}
          onClose={cleanActionData}
        />
      )}

      {isAction("authorized") && (
        <ModalAuthorizedPurchaseRequisition
          requisition={
            action.data as IPurchaseRequisition & { budgets: DraftOrder[] }
          }
          closeModal={cleanActionData}
        />
      )}

      {isAction("edit") && (
        <ModalEditPurchaseRequisition
          requisition={action.data as IPurchaseRequisition}
          closeModal={cleanActionData}
          onSaved={cleanActionData}
        />
      )}

      {isAction("view") && (
        <ModalViewPurchaseRequisition
          requisition={action.data as IPurchaseRequisition}
          closeModal={cleanActionData}
        />
      )}

      {isAction("order") && (
        <ModalGenerateOrder
          onClose={() => setAction({ eventName: "" })}
          requisition={action.data as IPurchaseRequisition}
        />
      )}

      {modal && (
        <ModalCreatePurchaseRequisition
          selectedItens={selectedItens}
          setSelectedItens={setSelectedItens}
          products={products}
          handleAddItemLine={handleAddItemLine}
          removeItem={removeItem}
          updateItem={updateItem}
          closeModal={closeModal}
        />
      )}
    </div>
  );
}
